import type { CVSection } from "./cv";
import type { ExperienceLocationType } from "./identity";

export interface JobCompanySummary {
  id: string;
  name: string;
  slug: string | null;
  logo: string | null;
}

export interface JobScreeningQuestion {
  id: string;
  kind: "prescreen" | "custom";
  prompt: string;
  tag: string | null;
}

/**
 * Where a job is listed. Lifecycle stays in `status`, confidential identity in
 * `visibility`/`confidentialCompanyName` — distribution is a third, separate
 * axis (PRD §7.5/§7.7).
 *
 * `tabbio_only` exists because confidential roles were never on the company
 * page: masking the employer and then linking the role from their own careers
 * page would undo the masking. That consequence used to live only in the
 * read queries, so the column claimed `tabbio_and_company` for a role that
 * could not reach the company page, and the wizard repeated the claim back
 * to the employer. Naming the state keeps `visibility` about identity alone.
 */
export type JobDistribution =
  | "tabbio_and_company"
  | "tabbio_only"
  | "company_page_only"
  | "hidden";

/**
 * Distributions the Tabbio job board and every surface fed by it (catalog,
 * discovery, matching, job-match notifications) list.
 *
 * Exported as one list so a query gates on membership rather than on a
 * hand-written literal: the old `distribution: 'tabbio_and_company'` filters
 * would each have silently dropped every `tabbio_only` role off the board.
 */
export const BOARD_DISTRIBUTIONS: readonly JobDistribution[] = [
  "tabbio_and_company",
  "tabbio_only",
];

/** Distributions the employer's own public company page renders. */
export const COMPANY_PAGE_DISTRIBUTIONS: readonly JobDistribution[] = [
  "tabbio_and_company",
  "company_page_only",
];

/** Distributions with any public surface at all, i.e. everything but hidden. */
export const PUBLIC_DISTRIBUTIONS: readonly JobDistribution[] = [
  "tabbio_and_company",
  "tabbio_only",
  "company_page_only",
];

/** Does this distribution put the role on the Tabbio job board? */
export function isBoardDistribution(value: JobDistribution): boolean {
  return BOARD_DISTRIBUTIONS.includes(value);
}

/**
 * The distribution that applies once identity masking is decided.
 *
 * A masked employer cannot be listed on their own company page - the page
 * names them - so `tabbio_and_company` collapses to `tabbio_only` the moment
 * masking is on. This lives in the shared package because the wizard and the
 * API must reach the same answer: when they disagreed, the wizard kept saying
 * "Tabbio + Company page" for a role the server had already taken off the
 * company page.
 */
export function resolveJobDistribution(
  distribution: JobDistribution,
  confidential: boolean,
): JobDistribution {
  return confidential && distribution === "tabbio_and_company"
    ? "tabbio_only"
    : distribution;
}

export type JobScreeningQuestionType =
  | "yes_no"
  | "multiple_choice"
  | "short_answer"
  | "long_answer"
  | "number";

export type JobDefaultQuestionKey =
  | "education"
  | "experience_years"
  | "expected_salary"
  | "availability";

/**
 * Typed screening question (v2). Stored inside a versioned envelope
 * `{ version: 2, questions: JobScreeningQuestionV2[] }` in
 * `Job.screeningQuestions`; v1 rows are upgraded at read time.
 */
export interface JobScreeningQuestionV2 {
  /** Stable unique id — never positional (v1's `custom:0` re-pointed answers). */
  id: string;
  prompt: string;
  type: JobScreeningQuestionType;
  required: boolean;
  order: number;
  /** multiple_choice only: at least two unique, non-empty options. */
  options?: string[];
  source: "default" | "custom";
  defaultKey?: JobDefaultQuestionKey;
}

export interface JobScreeningAnswerV2 {
  questionId: string;
  type: JobScreeningQuestionType;
  value: string | number | boolean;
}

/**
 * Where the candidate must already be, expressed relative to the job's own
 * country/city rather than as fixed places.
 *
 * The first five ids are the live set. The tail is the UAE-specific set this
 * replaced: stored jobs still hold those strings, so they stay in the union to
 * keep old rows valid and are folded into the live ids on hydration
 * (`LEGACY_CANDIDATE_LOCATION_IDS` in the post-job machine).
 */
export type JobEligibilityLocation =
  | "any"
  | "in_country"
  | "in_city"
  | "in_region"
  | "open_to_relocation"
  /** @deprecated pre-2026 UAE-specific ids, read-only. */
  | "uae"
  | "dubai"
  | "abu_dhabi"
  | "gcc"
  | "uae_relocation";

export type JobEligibilityExperience =
  | "any"
  | "1_plus"
  | "3_plus"
  | "5_plus"
  | "7_plus"
  | "10_plus";

export type JobEligibilityLanguage =
  | "any"
  | "english"
  | "arabic"
  | "english_and_arabic"
  | "english_or_arabic";

export type JobEligibilityWorkEligibility =
  | "any"
  | "eligible"
  | "eligible_or_sponsorship"
  | "sponsorship_required";

/**
 * Nationality a role is reserved for, asked only to support the GCC workforce
 * localisation quotas (Emiratisation, Saudization and their equivalents) that
 * make the requirement lawful. Every non-`none` value stays gated behind a
 * written policy note and an explicit acknowledgement.
 *
 * `emiratisation` / `saudization` are the programme-shaped ids this replaced;
 * they remain in the union so stored jobs keep validating and are folded into
 * `emirati` / `saudi` on hydration.
 */
export type JobNationalityRequirement =
  | "none"
  | "emirati"
  | "saudi"
  | "qatari"
  | "kuwaiti"
  | "bahraini"
  | "omani"
  | "gcc"
  | "other"
  /** @deprecated pre-2026 programme ids, read-only. */
  | "emiratisation"
  | "saudization";

/** @deprecated Renamed to {@link JobNationalityRequirement}. */
export type JobWorkforceProgramme = JobNationalityRequirement;

/** Candidate requirements (PRD §7.4). No protected attributes. */
export interface JobEligibility {
  candidateLocation?: JobEligibilityLocation;
  experienceYears?: JobEligibilityExperience;
  language?: JobEligibilityLanguage;
  workEligibility?: JobEligibilityWorkEligibility;
  /**
   * Nationality requirement. The stored key keeps its original name so rows
   * written before the rename keep resolving; the field is surfaced to
   * employers as "Nationality".
   */
  workforceProgramme?: {
    value: JobNationalityRequirement;
    /** Policy/approval note, at least 20 characters when value !== 'none'. */
    note: string;
    acknowledged: true;
    acknowledgedBy: string;
    acknowledgedAt: string;
  };
}

export interface JobScreeningAnswerInput {
  questionId: string;
  answer: string;
}

export interface JobScreeningAnswer {
  questionId: string;
  kind: "prescreen" | "custom";
  prompt: string;
  answer: string;
}

export type CompanyJobApplyMethod = "tabbio" | "external";

export type CompanyJobWorkType = "onsite" | "hybrid" | "remote";

export type CompanyJobEmployment =
  | "full_time"
  | "part_time"
  | "contract"
  | "internship";

export type CompanyJobExperienceLevel =
  | "entry_0_2"
  | "mid_2_5"
  | "senior_5_8"
  | "lead_8_12"
  | "executive_12_plus";

export interface CreateCompanyJobInput {
  companyId: string;
  rawInput?: string;
  title: string;
  generatedJD: string;
  department?: string;
  country: string;
  city?: string;
  workType: CompanyJobWorkType;
  employment: CompanyJobEmployment;
  experienceLevel: CompanyJobExperienceLevel;
  applyMethod: CompanyJobApplyMethod;
  externalUrl?: string;
  nationality?: string;
  gender?: string;
  genderReason?: string;
  yearsExperience?: string;
  minQualification?: string;
  prescreens?: string[];
  customQuestions?: string[];
}

export interface GenerateCompanyJobDraftInput {
  rawInput: string;
  locale?: "en" | "ar";
}

export interface GenerateCompanyJobDraftResult {
  title: string;
  department: string;
  country: string;
  city: string;
  workType: CompanyJobWorkType;
  employment: CompanyJobEmployment;
  experienceLevel: CompanyJobExperienceLevel;
  generatedJD: string;
  /** v2 four-step draft shape; legacy flat fields above remain for old clients. */
  contract?: "v2";
  basics?: {
    title: string;
    department?: string;
    country?: string;
    city?: string;
    workMode?: string;
    employment?: string;
    seniority?: string;
  };
  content?: {
    summary: string;
    responsibilities: string[];
    requirements: string[];
    niceToHave: string[];
  };
  suggestedQuestion?: JobScreeningQuestionV2;
}

export interface CompanyJobSummary {
  expiresAt?: string | null;
  id: string;
  companyId: string;
  company: JobCompanySummary;
  title: string;
  description: string;
  location: string | null;
  lat?: number | null;
  lng?: number | null;
  type: string;
  workMode: string;
  level: string;
  department: string | null;
  requirements: string[];
  tags: string[];
  applicationMethod: CompanyJobApplyMethod | null;
  applicationUrl: string | null;
  easyApply: boolean;
  status: string;
  postedAt: string | null;
  createdAt: string;
  updatedAt: string;
  screeningQuestions: JobScreeningQuestion[];
  applicationCount: number;
  visibility?: "public" | "confidential" | "private";
  salary?: SalaryRange | null;
  salaryVisible?: boolean;
  publicSlug?: string;
  canonicalUrl?: string;
  applyUrl?: string | null;
  indexingAllowed?: boolean;
  distribution?: JobDistribution;
  niceToHave?: string[];
  responsibilities?: string[];
  benefits?: string[];
  city?: string | null;
  country?: string | null;
  screeningQuestionsV2?: JobScreeningQuestionV2[];
  /** Public responses reduce workforceProgramme to `{ value }`; note/ack are employer-only. */
  eligibility?: JobEligibility;
  /** Employer-view only. */
  archivedAt?: string | null;
}

export interface CompanyJobListResult {
  jobs: CompanyJobSummary[];
  total: number;
}

export type JobDiscoveryDatePosted = "today" | "thisWeek" | "thisMonth";

export interface JobDiscoveryInput {
  query?: string;
  /**
   * Broad location tokens (free text or a picked country): matched against
   * location/city/country. A picked country here scopes to the whole country.
   */
  locations?: string[];
  /**
   * Precise city tokens (from a "City, Country" pick): matched against the
   * city/location fields only, so a city selection stays scoped to that city
   * and never broadens to the whole country.
   */
  cities?: string[];
  workModes?: CompanyJobWorkType[];
  employmentTypes?: string[];
  datePosted?: JobDiscoveryDatePosted;
  page?: number;
  limit?: number;
}

export interface JobDiscoveryItem extends CompanyJobSummary {
  matchScore: number;
  matchReasons: string[];
  applied: boolean;
  appliedAt: string | null;
}

export interface JobDiscoveryResult {
  jobs: JobDiscoveryItem[];
  total: number;
  page: number;
  limit: number;
}

export type CatalogJobSource =
  | "tabbio_native"
  | "google_jobs_dataforseo"
  | "partner_ats"
  | "ats_workable";

export type CatalogJobApplyMode =
  | "tabbio_native"
  | "external_redirect"
  | "partner_ats_managed";

export type CatalogJobStatus = "active" | "stale" | "closed" | "archived";

export type CompanyAtsProvider = "greenhouse" | "lever" | "ashby";

export interface CatalogJobSourceInfo {
  key: CatalogJobSource | string;
  label: string;
  trustRank: number;
  sourceJobId?: string | null;
  sourceUrl?: string | null;
  directApplyUrl?: string | null;
  lastSeenAt?: string | null;
  sourceUpdatedAt?: string | null;
}

export interface CatalogJobUserStateSummary {
  saved: boolean;
  savedAt: string | null;
  hidden: boolean;
  hiddenAt: string | null;
}

export interface JobSearchConstraints {
  countries?: string[];
  cities?: string[];
  seniorityLevels?: string[];
  excludedRoles?: string[];
  excludedCompanies?: string[];
  minimumSalary?: number | null;
  salaryCurrency?: string;
  salaryPeriod?: "monthly" | "yearly";
  includeUnknownSalary?: boolean;
}

export interface CatalogJobDiscoveryInput extends JobDiscoveryInput {
  /** Server-resolved constraints, never accepted from public query payloads. */
  countryCodes?: string[];
  /** Server-resolved candidate sampling; never accepted from query payloads. */
  preferGulfCandidates?: boolean;
  candidateRoleFamilies?: string[];
  candidateRoleTerms?: string[];
  /** Server-resolved senior ATS recall; final relevance gates still apply. */
  candidateLeadership?: boolean;
  strictMatching?: boolean;
  jobAlertMatching?: boolean;
  searchConstraints?: JobSearchConstraints;

  /**
   * Restrict to sources Tabbio is authorized to publish, for surfaces that
   * assert publication to a search engine (sitemap, JobPosting markup). Distinct
   * from `sources`, which is a caller-supplied filter, and from
   * `surfaceEligibleOnly`, which is about content quality rather than permission.
   */
  indexableSourcesOnly?: boolean;
  sources?: CatalogJobSource[];
  applyModes?: CatalogJobApplyMode[];
  includeLowConfidence?: boolean;
  surfaceEligibleOnly?: boolean;
  cvId?: string;
  companies?: string[];
}

export interface CatalogJobListItem {
  id: string;
  source: CatalogJobSource | string;
  sourceInfo: CatalogJobSourceInfo;
  applyMode: CatalogJobApplyMode;
  status: CatalogJobStatus | string;
  visibility?: "public" | "confidential" | "private";
  isExternal: boolean;
  company: JobCompanySummary;
  title: string;
  summary: string | null;
  roleFamily?: string | null;
  roleKey?: string | null;
  roleConfidence?: number | null;
  location: string | null;
  lat?: number | null;
  lng?: number | null;
  country: string | null;
  city: string | null;
  type: string | null;
  workMode: string | null;
  level: string | null;
  matchedSkills?: string[];
  missingSkills?: string[];
  contentCompleteness?: number | null;
  qualityFlags?: string[];
  surfaceEligible?: boolean;
  applicationUrl: string | null;
  easyApply: boolean;
  postedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  matchScore: number | null;
  matchReasons: string[];
  applied: boolean;
  appliedAt: string | null;
  userState: CatalogJobUserStateSummary;
  publicSlug?: string;
  canonicalUrl?: string;
  applyUrl?: string | null;
  indexingAllowed?: boolean;
  /**
   * Set on an external posting the catalog has consolidated onto another row:
   * the id of the winner of its duplicate cluster. Absent on everything else,
   * which is the overwhelming majority.
   *
   * Consumers should not render this. It exists so the public detail route can
   * resolve `canonicalSlug` - see `PublicJobDetail.canonicalSlug`.
   */
  canonicalPostingId?: string | null;
}

export interface CatalogJobSummary extends CatalogJobListItem {}

export interface PublicJobListItem extends CatalogJobListItem {
  publicSlug: string;
  canonicalUrl: string;
  applyUrl: string | null;
  indexingAllowed: boolean;
}

export interface CatalogJobDetail extends CatalogJobListItem {
  description: string;
  salary: SalaryRange | null;
  requirements: string[];
  tags: string[];
  responsibilities: string[];
  benefits: string[];
  normalizedSkills: string[];
  screeningQuestions: JobScreeningQuestion[];
  /**
   * Typed questions (v2). Present for Tabbio-posted roles; absent for external
   * postings, which carry no questions at all. The apply UI needs these to
   * render the right control (and a multiple choice question's options) —
   * `screeningQuestions` alone flattens every type to a bare prompt.
   */
  screeningQuestionsV2?: JobScreeningQuestionV2[];
  sourceOptions: CatalogJobSourceInfo[];
  partnerAtsProvider: CompanyAtsProvider | null;
  companyWebsite: string | null;
  companyLocation: string | null;
  searchText: string | null;
  fieldProvenance?: Record<string, string[]>;
  companyMetadata?: Record<string, unknown>;
}

export interface PublicJobDetail extends CatalogJobDetail {
  publicSlug: string;
  /**
   * The slug `rel=canonical` should point at, which is normally `publicSlug`
   * itself.
   *
   * The jobs feed used to mint a fresh row for an opening it had already stored
   * every time the posting's derived "posted N days ago" date crossed a
   * fortnight boundary, so one opening ended up behind up to nineteen live URLs
   * with identical titles, meta descriptions and bodies. Those URLs all stay
   * live - the project rule is that the indexed surface never shrinks - so
   * instead each one names the original here and the cluster resolves to a
   * single indexable target.
   */
  canonicalSlug: string;
  canonicalUrl: string;
  applyUrl: string | null;
  indexingAllowed: boolean;
  /**
   * `closed` means the posting existed and has ended, so the URL still resolves
   * instead of 404ing. A job that was never public - private, or absent - is a
   * genuine 404 and never reaches a consumer.
   *
   * The distinction exists because the jobs catalogue turns over roughly 45%
   * within hours, and hard-404ing every retired posting sent thousands of dead
   * URLs to Google. It is also what the indexation contract already required:
   * excluded pages stay reachable as `noindex,follow` rather than being deleted.
   */
  publicState: "open" | "closed";
  /** Actual language of all persisted, visible job fields in this response. */
  contentLanguage?: "en" | "ar";
  /** Locales whose exact current content is eligible for indexation. */
  indexableLocales?: Array<"en" | "ar">;
}

export interface PublicJobListResult {
  jobs: PublicJobListItem[];
  total: number;
  page: number;
  limit: number;
}

/**
 * One entry in the related-jobs module rendered inside a job detail page's body.
 *
 * Deliberately NOT `PublicJobListItem`. The module is a short in-body link list
 * (5-8 anchors, never more - see `RELATED_PUBLIC_JOBS_MAX`), so it needs the
 * anchor text and the two qualifiers that make a link worth clicking, and
 * nothing else. Serving a full detail/list payload per related job would ship
 * salary, requirements, screening questions and per-user state to render one
 * line of text.
 *
 * Every entry is guaranteed to be an OPEN, indexable posting at the moment the
 * page was rendered: the selection query applies the same public predicates the
 * board itself does. A live posting must never link to a closed one - a closed
 * posting renders `noindex,follow`, so the edge is spent on a page that passes
 * nothing and the click lands a job seeker on a role that has ended.
 */
export interface PublicRelatedJob {
  /** Catalog job id (`native:<id>` / `external:<id>`). */
  id: string;
  /** Slug for `/{locale}/jobs/{slug}` - built by the one slug owner. */
  slug: string;
  /** The link's anchor text. User-generated: render with `dir="auto"`. */
  title: string;
  /** User-generated: render with `dir="auto"`. */
  companyName: string;
  /** City, else country, else the raw location string. May be absent. */
  location: string | null;
  /** Raw employment type / work mode tokens; the client humanizes them. */
  type: string | null;
  workMode: string | null;
  postedAt: string | null;
}

export interface PublicJobFacetBucket {
  value: string;
  count: number;
}

export interface PublicJobFacets {
  locations: PublicJobFacetBucket[];
  companies: PublicJobFacetBucket[];
  employmentTypes: PublicJobFacetBucket[];
  workModes: PublicJobFacetBucket[];
}

export interface PublicJobFacetsResult extends PublicJobFacets {
  total: number;
}

export interface CatalogJobDiscoveryResult {
  jobs: CatalogJobListItem[];
  total: number;
  page: number;
  limit: number;
  noProfileFit?: boolean;
}

export interface ApplyToJobInput {
  cvId?: string;
  cvSelection?: import("./cv-release").CvDocumentSelection;
  expectedDisclosureHash?: string;
  screeningAnswers: JobScreeningAnswerInput[];
  /** Typed v2 answers; preferred over screeningAnswers on new clients. */
  answers?: JobScreeningAnswerV2[];
  idempotencyKey?: string;
  consent?: { version: string; accepted: true; text?: string };
  source?: "tabbio_app" | "public_page";
}

export interface CatalogApplyInput {
  cvId?: string;
  cvSelection?: import("./cv-release").CvDocumentSelection;
  coverLetter?: string;
  screeningAnswers?: JobScreeningAnswerInput[];
}

export interface CatalogTrackRedirectInput extends CatalogApplyInput {
  redirectUrl?: string;
}

export type CatalogConfirmApplyOutcome = "submitted" | "not_applied";

export interface CatalogConfirmApplyInput {
  outcome: CatalogConfirmApplyOutcome;
}

export interface SalaryRange {
  min?: number;
  max?: number;
  currency?: string;
  period?: "monthly" | "yearly";
}

/**
 * One append-only submission receipt (audit E02): what one recipient received
 * for one submission of a job application or a talent network membership. A
 * resubmission appends a receipt; an earlier one is never rewritten. The
 * frozen CV document itself stays server-side: a summary only.
 */
export interface SubmissionReceiptSummary {
  id: string;
  kind: "job_application" | "talent_network";
  /** 1 for the first submission of the application or membership, then 2, 3, ... */
  sequence: number;
  /** The newest receipt: the submission the row's current evidence reflects. */
  current: boolean;
  /**
   * "submit": written with the submission. "backfill": copied later from the
   * evidence the row already held before receipts existed.
   */
  origin: "submit" | "backfill";
  submittedAt: string;
  /** "tabbio_app" | "public_page" | "public_new_user" */
  source: string;
  /** The CV as sent (its title then, not now); null when no CV document was sent. */
  cv: { id: string | null; title: string | null; locale: string | null } | null;
  /** Whether a frozen CV document is stored with this receipt. */
  hasDocument: boolean;
  /** The uploaded resume sent with this submission (public new-candidate flow). */
  resume: { filename: string | null } | null;
  /** Consent exactly as given with this submission. */
  consent: {
    version: string | null;
    text: string | null;
    acceptedAt: string | null;
  } | null;
  supersedesReceiptId: string | null;
}

/**
 * "recorded": the row has receipts. "historical": it predates receipts and has
 * none; its document is the stored snapshot or, older still, the live CV
 * (`submittedCvDocument.snapshot === false`). Never fabricated.
 */
export type SubmissionReceiptsStatus = "recorded" | "historical";

/**
 * Additive receipt fields on an application or membership payload. Absent
 * when the server could not tell (receipts switched off or unreadable); old
 * clients ignore them.
 */
export interface SubmissionReceiptFields {
  /** Newest first. */
  receipts?: SubmissionReceiptSummary[];
  receiptsStatus?: SubmissionReceiptsStatus;
}

export interface CatalogApplicationRecord extends SubmissionReceiptFields {
  id: string;
  kind: "native" | "external";
  nativeApplicationId?: string | null;
  externalApplyIntentId?: string | null;
  catalogJobId: string;
  source: CatalogJobSource | string;
  applyMode: CatalogJobApplyMode;
  status: string;
  appliedAt: string;
  updatedAt: string;
  redirectUrl: string | null;
  applicationUrl: string | null;
  job: CatalogJobSummary;
  cv: {
    id: string;
    title: string;
    updatedAt: string;
  } | null;
}

export interface CatalogApplyPreparation {
  job: CatalogJobDetail;
  cv: {
    id: string;
    title: string;
    updatedAt: string;
  } | null;
  existingApplication: CatalogApplicationRecord | null;
  readyToSubmit: boolean;
  redirectRequired: boolean;
  redirectUrl: string | null;
  coverLetter: string | null;
  screeningQuestions: JobScreeningQuestion[];
  notes: string[];
}

export interface CatalogApplySubmissionResult {
  outcome: "submitted" | "redirect_required" | "blocked";
  application: CatalogApplicationRecord | null;
  redirectUrl: string | null;
  message: string | null;
}

export type AutoApplyRuleFrequency = "daily" | "weekly" | "instant" | "monthly";

export type AutoApplyRuleKey =
  | "job-alerts"
  | "auto-apply"
  | "market-report"
  | "profile-views"
  | "salary-insights";

export type AutoApplyCandidateStatus =
  | "matched"
  | "eligible_auto_apply"
  | "needs_review"
  | "auto_applied"
  | "skipped"
  | "failed"
  | "dismissed";

export type AutoApplyScreeningResolution =
  | "ready"
  | "manual_questions"
  | "cover_letter_required"
  | "missing_cv"
  | "already_applied"
  | "own_company"
  | "job_inactive"
  | "unsupported_source"
  | "unknown";

export type JobMatchDeliveryChannel = "in_app" | "push" | "email_digest";

export interface AutoApplyRuleSettings {
  minMatchScore: number;
  defaultCvId: string | null;
  sources: CatalogJobSource[];
  locations: string[];
  workModes: CompanyJobWorkType[];
  employmentTypes: string[];
  pushEnabled: boolean;
  emailDigestEnabled: boolean;
  similarJobsEnabled: boolean;
  digestHourLocal: number;
  timezone: string;
}

export interface AutoApplyRuleSummary {
  id: string;
  key: AutoApplyRuleKey | string;
  name: string;
  enabled: boolean;
  frequency: AutoApplyRuleFrequency | string;
  settings: AutoApplyRuleSettings;
  isLive: boolean;
  comingSoon: boolean;
  lastRunAt: string | null;
  lastResultLabel: string | null;
}

export interface AutoApplyRunSummary {
  id: string;
  ruleId: string;
  status: string;
  triggerSource: string | null;
  discovered: number;
  prepared: number;
  submitted: number;
  skipped: number;
  failed: number;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface AutoApplyQueueRecord {
  id: string;
  ruleId: string | null;
  runId: string | null;
  catalogJobId: string;
  applyMode: CatalogJobApplyMode;
  status: string;
  matchScore: number | null;
  matchReasons: string[];
  readyToSubmit: boolean;
  applicationId: string | null;
  screeningResolution: AutoApplyScreeningResolution | string | null;
  existingApplicationId: string | null;
  errorMessage: string | null;
  job: CatalogJobSummary;
  cv: {
    id: string;
    title: string;
    updatedAt: string;
  } | null;
}

export interface AutoApplyActivityRecord {
  id: string;
  ruleId: string | null;
  ruleKey: AutoApplyRuleKey | string;
  catalogJobId: string;
  applyMode: CatalogJobApplyMode;
  status: AutoApplyCandidateStatus | string;
  matchScore: number | null;
  matchReasons: string[];
  sourceTrigger: string | null;
  firstMatchedAt: string;
  lastMatchedAt: string;
  lastStatusChangedAt: string;
  screeningResolution: AutoApplyScreeningResolution | string | null;
  applicationId: string | null;
  errorMessage: string | null;
  deliveredChannels: JobMatchDeliveryChannel[];
  job: CatalogJobSummary;
  cv: {
    id: string;
    title: string;
    updatedAt: string;
  } | null;
}

export interface AutoApplyOverviewCounts {
  matched: number;
  eligibleAutoApply: number;
  needsReview: number;
  autoApplied: number;
  skipped: number;
  failed: number;
  dismissed: number;
}

export interface AutoApplyOverview {
  rules: AutoApplyRuleSummary[];
  latestRun: AutoApplyRunSummary | null;
  counts: AutoApplyOverviewCounts;
  activity: AutoApplyActivityRecord[];
  queue: AutoApplyQueueRecord[];
}

export interface UpdateAutoApplyRuleInput {
  enabled?: boolean;
  frequency?: AutoApplyRuleFrequency;
  settings?: Partial<AutoApplyRuleSettings>;
}

export interface JobApplication {
  id: string;
  jobId: string;
  userId: string;
  companyId: string;
  cvId?: string | null;
  coverLetter?: string | null;
  status: ApplicationStatus;
  appliedAt: string;
  updatedAt: string;
}

export interface JobApplicationSummary
  extends JobApplication, SubmissionReceiptFields {
  screeningAnswers: JobScreeningAnswer[];
  job: CompanyJobSummary;
  company: JobCompanySummary;
  cv: {
    id: string;
    title: string;
    updatedAt: string;
  } | null;
}

export type ApplicationStatus =
  | "applied"
  | "reviewed"
  | "shortlisted"
  | "interview"
  | "offered"
  | "rejected"
  | "withdrawn";

export type EmployerApplicantLibraryStage =
  | "inbox"
  | "screening"
  | "interview"
  | "offer";

export interface ApplicationStatusHistoryEntry {
  status: ApplicationStatus;
  at: string;
  byUserId?: string | null;
}

export interface EmployerApplicantCandidateSummary {
  userId: string;
  name: string;
  username: string | null;
  image: string | null;
  email: string;
  title: string | null;
  location: string | null;
  contactEmail: string | null;
  phone: string | null;
  nationality: string | null;
}

export interface EmployerApplicantSummary {
  applicationId: string;
  jobId: string;
  companyId: string;
  status: ApplicationStatus;
  libraryStage: EmployerApplicantLibraryStage;
  /** Submission provenance: 'tabbio_app' | 'public_page' | 'public_new_user'. */
  source?: string;
  appliedAt: string;
  updatedAt: string;
  screeningAnswers: JobScreeningAnswer[];
  statusHistory: ApplicationStatusHistoryEntry[];
  candidate: EmployerApplicantCandidateSummary;
  submittedCv: {
    id: string;
    title: string;
    updatedAt: string;
  } | null;
}

export interface EmployerApplicantListResult {
  applicants: EmployerApplicantSummary[];
  counts: {
    total: number;
    applied: number;
    reviewed: number;
    shortlisted: number;
    interview: number;
    offered: number;
    rejected: number;
    withdrawn: number;
  };
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface EmployerApplicantCvSnapshot {
  id: string;
  title: string;
  summary: string | null;
  templateId: string;
  sections: CVSection[];
  isPublic: boolean;
  slug: string | null;
  createdAt: string;
  updatedAt: string;
  isSubmitted: boolean;
  /**
   * True when the document is the immutable submitted snapshot; false when a
   * pre-snapshot application fell back to the candidate's live CV (UIs label
   * that state "Current CV").
   */
  snapshot?: boolean;
}

export interface EmployerApplicantExperience {
  id: string;
  company: string;
  title: string;
  location: string | null;
  locationType: ExperienceLocationType | null;
  employmentType: string | null;
  startDate: string | null;
  endDate: string | null;
  current: boolean;
  description: string | null;
  achievements: string[];
}

export interface EmployerApplicantEducation {
  id: string;
  school: string;
  degree: string | null;
  fieldOfStudy: string | null;
  startDate: string | null;
  endDate: string | null;
  grade: string | null;
  description: string | null;
}

export interface EmployerApplicantCertification {
  id: string;
  name: string;
  issuer: string;
  issueDate: string | null;
  expirationDate: string | null;
  credentialId: string | null;
  credentialUrl: string | null;
}

export interface EmployerApplicantHighlight {
  id: string;
  title: string;
  description: string | null;
  mediaUrl: string | null;
  mediaType: string | null;
  link: string | null;
  date: string | null;
}

export interface EmployerApplicantProject {
  id: string;
  title: string;
  description: string | null;
  image: string | null;
  link: string | null;
  technologies: string[];
}

export interface EmployerApplicantDetail
  extends EmployerApplicantSummary, SubmissionReceiptFields {
  job: CompanyJobSummary;
  company: JobCompanySummary;
  coverLetter: string | null;
  conversationId: string | null;
  profileAccess?: "submitted_snapshot" | "live_profile";
  candidateProfile: {
    bio: string | null;
    about: string | null;
    skills: string[];
    interests: string[];
    experience: EmployerApplicantExperience[];
    education: EmployerApplicantEducation[];
    certifications: EmployerApplicantCertification[];
    highlights: EmployerApplicantHighlight[];
    projects: EmployerApplicantProject[];
  };
  submittedCvDocument: EmployerApplicantCvSnapshot | null;
  /**
   * @deprecated Always empty. Only the submitted CV is shared with an
   * employer; the key stays so older clients keep parsing.
   */
  additionalCvDocuments: EmployerApplicantCvSnapshot[];
  /** Uploaded resume file from public new-user applications. */
  submittedResume?: { filename: string | null; available: boolean } | null;
}

export interface EmployerApplicationStatusUpdateInput {
  status: Extract<
    ApplicationStatus,
    "reviewed" | "shortlisted" | "interview" | "offered" | "rejected"
  >;
}

export interface EmployerApplicantMessageInput {
  content: string;
}

export type Job = CompanyJobSummary;
export type JobType = CompanyJobEmployment;
export type WorkMode = CompanyJobWorkType;
export type ExperienceLevel = CompanyJobExperienceLevel;
export type JobStatus = "draft" | "active" | "closed" | "archived";
export type JobApplicationMethod = CompanyJobApplyMethod;
export type CompanyJobListItem = CompanyJobSummary;
export type CreateJobInput = CreateCompanyJobInput;
